/**
 * HR Service
 * Staff profile management, credential tracking, and training compliance
 * Supports expiry alerting and automatic rostering block
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  createStaffProfile,
  getCredentialAlerts,
  isEligibleForRostering,
  STAFF_ROLES,
  MANDATORY_TRAINING
} from '../../data/models/StaffProfile';

class HRService {
  constructor() {
    this.storageKey = 'hr_staff_profiles';
    this.initialized = false;
    this.initializeMockData();
  }

  initializeMockData() {
    const existing = LocalStorageService.get(this.storageKey, []);
    if (existing.length > 0) {
      this.initialized = true;
      return;
    }

    const now = new Date();
    const mockStaff = [
      { firstName: 'Sarah', lastName: 'Mitchell', roleCode: 'FM', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'sarah.mitchell@medfastcrm.com', phone: '+61412345001' },
      { firstName: 'James', lastName: 'Chen', roleCode: 'RN', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'james.chen@medfastcrm.com', phone: '+61412345002' },
      { firstName: 'Emma', lastName: 'Thompson', roleCode: 'RN', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'emma.thompson@medfastcrm.com', phone: '+61412345003' },
      { firstName: 'David', lastName: 'Rodriguez', roleCode: 'EN', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'part_time', email: 'david.rodriguez@medfastcrm.com', phone: '+61412345004' },
      { firstName: 'Lisa', lastName: 'Park', roleCode: 'EN', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'casual', employmentStatus: 'casual', email: 'lisa.park@medfastcrm.com', phone: '+61412345005' },
      { firstName: 'Michael', lastName: 'Brown', roleCode: 'PCA', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'michael.brown@medfastcrm.com', phone: '+61412345006' },
      { firstName: 'Sophie', lastName: 'Wilson', roleCode: 'PCA', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'sophie.wilson@medfastcrm.com', phone: '+61412345007' },
      { firstName: 'Andrew', lastName: 'Taylor', roleCode: 'PCA', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'agency', employmentStatus: 'casual', email: 'andrew.taylor@agency.com', phone: '+61412345008' },
      { firstName: 'Rachel', lastName: 'Kim', roleCode: 'PHYSIO', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'contract', employmentStatus: 'part_time', email: 'rachel.kim@medfastcrm.com', phone: '+61412345009' },
      { firstName: 'Tom', lastName: 'Hughes', roleCode: 'OT', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'contract', employmentStatus: 'part_time', email: 'tom.hughes@medfastcrm.com', phone: '+61412345010' },
      { firstName: 'Grace', lastName: 'Lee', roleCode: 'RN', facilityId: 'bayview', facilityName: 'Bayview Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'grace.lee@medfastcrm.com', phone: '+61412345011' },
      { firstName: 'Daniel', lastName: 'Wang', roleCode: 'EN', facilityId: 'bayview', facilityName: 'Bayview Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'daniel.wang@medfastcrm.com', phone: '+61412345012' },
      { firstName: 'Hannah', lastName: 'Smith', roleCode: 'PCA', facilityId: 'bayview', facilityName: 'Bayview Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'hannah.smith@medfastcrm.com', phone: '+61412345013' },
      { firstName: 'Mark', lastName: 'Johnson', roleCode: 'PCA', facilityId: 'bayview', facilityName: 'Bayview Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'mark.johnson@medfastcrm.com', phone: '+61412345014' },
      { firstName: 'Olivia', lastName: 'Davis', roleCode: 'LSO', facilityId: 'sunshine', facilityName: 'Sunshine Gardens', employmentType: 'permanent', employmentStatus: 'full_time', email: 'olivia.davis@medfastcrm.com', phone: '+61412345015' }
    ];

    mockStaff.forEach((s, idx) => {
      const startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - Math.floor(Math.random() * 36) - 6);

      const ahpraExpiry = new Date(now);
      const expiryOffset = idx === 2 ? 5 : idx === 4 ? -3 : idx === 3 ? 25 : Math.floor(Math.random() * 300) + 30;
      ahpraExpiry.setDate(ahpraExpiry.getDate() + expiryOffset);

      const workerScreenExpiry = new Date(now);
      workerScreenExpiry.setDate(workerScreenExpiry.getDate() + (idx === 7 ? 15 : Math.floor(Math.random() * 600) + 60));

      const policeCheckExpiry = new Date(now);
      policeCheckExpiry.setDate(policeCheckExpiry.getDate() + (idx === 5 ? -10 : Math.floor(Math.random() * 700) + 90));

      const roleConfig = STAFF_ROLES[s.roleCode];

      const profile = createStaffProfile({
        ...s,
        roleName: roleConfig?.name || s.roleCode,
        startDate: startDate.toISOString().split('T')[0],
        ahpra: roleConfig?.requiresAHPRA ? {
          registrationNumber: `AHP${String(idx + 1).padStart(8, '0')}`,
          registrationType: 'general',
          division: s.roleCode === 'RN' || s.roleCode === 'EN' ? 'Nursing and Midwifery' : 'Allied Health',
          expiryDate: ahpraExpiry.toISOString().split('T')[0],
          verifiedDate: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          verifiedBy: 'HR Admin',
          status: expiryOffset <= 0 ? 'expired' : 'current'
        } : { registrationNumber: '', registrationType: '', division: '', expiryDate: null, verifiedDate: null, verifiedBy: null, status: 'not_applicable' },
        workerScreening: {
          checkDate: new Date(now.getTime() - 200 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          expiryDate: workerScreenExpiry.toISOString().split('T')[0],
          clearanceNumber: `WS${Date.now().toString().slice(-8)}-${idx}`,
          status: 'cleared',
          type: 'aged_care'
        },
        policeCheck: {
          checkDate: new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          expiryDate: policeCheckExpiry.toISOString().split('T')[0],
          referenceNumber: `PC${Date.now().toString().slice(-8)}-${idx}`,
          status: (idx === 5) ? 'expired' : 'cleared',
          issuingState: 'VIC'
        },
        rightToWork: {
          verified: true,
          verifiedDate: startDate.toISOString().split('T')[0],
          documentType: 'passport',
          documentNumber: `P${String(idx + 1).padStart(7, '0')}`,
          expiryDate: new Date(now.getTime() + 365 * 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          restrictions: ''
        },
        vaccinations: {
          influenza: {
            date: new Date(now.getFullYear(), 2, 15 + idx).toISOString().split('T')[0],
            expiryDate: new Date(now.getFullYear() + 1, 2, 15 + idx).toISOString().split('T')[0],
            batchNumber: `FLU-${now.getFullYear()}-${String(idx + 1).padStart(4, '0')}`,
            status: 'current'
          },
          covid19: {
            dose1Date: '2021-08-15',
            dose2Date: '2021-09-15',
            boosterDate: '2024-03-20',
            status: 'current'
          },
          hepatitisB: { date: '2019-06-01', status: 'current' },
          other: []
        },
        qualifications: this._generateQualifications(s.roleCode),
        trainingRecords: MANDATORY_TRAINING.map((t, ti) => {
          const completedDate = new Date(now);
          completedDate.setMonth(completedDate.getMonth() - Math.floor(Math.random() * 10));
          const expiryDate = new Date(completedDate);
          if (t.frequency === 'annual') expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          else if (t.frequency === 'biennial') expiryDate.setFullYear(expiryDate.getFullYear() + 2);
          else if (t.frequency === 'triennial') expiryDate.setFullYear(expiryDate.getFullYear() + 3);

          const expired = expiryDate < now;
          return {
            trainingId: t.id,
            trainingName: t.name,
            completedDate: completedDate.toISOString().split('T')[0],
            expiryDate: expiryDate.toISOString().split('T')[0],
            score: Math.floor(Math.random() * 15) + 85,
            certificateNumber: `CERT-${t.id.toUpperCase()}-${idx + 1}`,
            status: expired ? 'expired' : 'completed',
            frequency: t.frequency
          };
        }),
        emergencyContact: {
          name: `Contact for ${s.firstName}`,
          relationship: ['Spouse', 'Parent', 'Sibling'][idx % 3],
          phone: `+614${String(Math.floor(Math.random() * 100000000)).padStart(8, '0')}`,
          email: `emergency.${s.firstName.toLowerCase()}@email.com`
        }
      });

      LocalStorageService.addItem(this.storageKey, profile);
    });

    this.initialized = true;
    console.log(`[HRService] Initialized ${mockStaff.length} staff profiles`);
  }

  _generateQualifications(roleCode) {
    const qualsByRole = {
      RN: [{ name: 'Bachelor of Nursing', institution: 'University of Melbourne', year: 2018 }, { name: 'Graduate Certificate Aged Care', institution: 'Deakin University', year: 2020 }],
      EN: [{ name: 'Diploma of Nursing', institution: 'TAFE Victoria', year: 2019 }],
      PCA: [{ name: 'Certificate III Individual Support (Ageing)', institution: 'TAFE Victoria', year: 2021 }],
      AIN: [{ name: 'Certificate III Individual Support', institution: 'TAFE NSW', year: 2020 }],
      PHYSIO: [{ name: 'Bachelor of Physiotherapy', institution: 'Monash University', year: 2017 }],
      OT: [{ name: 'Bachelor of Occupational Therapy', institution: 'La Trobe University', year: 2018 }],
      FM: [{ name: 'Bachelor of Health Administration', institution: 'Griffith University', year: 2015 }, { name: 'MBA Healthcare Management', institution: 'UNSW', year: 2019 }],
      LSO: [{ name: 'Certificate IV Leisure & Health', institution: 'TAFE Victoria', year: 2020 }]
    };
    return qualsByRole[roleCode] || [];
  }

  async getAll(facilityId = null) {
    try {
      let profiles = LocalStorageService.get(this.storageKey, []);
      if (facilityId) profiles = profiles.filter(p => p.facilityId === facilityId);
      return { success: true, data: profiles };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getById(staffId) {
    try {
      const profile = LocalStorageService.findById(this.storageKey, staffId);
      return profile ? { success: true, data: profile } : { success: false, error: 'Staff profile not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async create(profileData) {
    try {
      const profile = createStaffProfile(profileData);
      const created = LocalStorageService.addItem(this.storageKey, profile);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async update(staffId, updates) {
    try {
      const updated = LocalStorageService.updateItem(this.storageKey, staffId, { ...updates, updatedAt: new Date().toISOString() });
      return updated ? { success: true, data: updated } : { success: false, error: 'Staff profile not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async delete(staffId) {
    try {
      const result = LocalStorageService.removeItem(this.storageKey, staffId);
      return { success: result, error: result ? null : 'Staff profile not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAllCredentialAlerts(facilityId = null) {
    try {
      const result = await this.getAll(facilityId);
      if (!result.success) return result;

      const allAlerts = result.data.flatMap(profile => getCredentialAlerts(profile));
      allAlerts.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

      return {
        success: true,
        data: {
          alerts: allAlerts,
          expired: allAlerts.filter(a => a.severity === 'expired'),
          critical: allAlerts.filter(a => a.severity === 'critical'),
          warning: allAlerts.filter(a => a.severity === 'warning'),
          info: allAlerts.filter(a => a.severity === 'info'),
          staffBlocked: allAlerts.filter(a => a.blockRostering).map(a => a.staffId).filter((v, i, a) => a.indexOf(v) === i)
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async checkRosterEligibility(staffId) {
    try {
      const result = await this.getById(staffId);
      if (!result.success) return result;
      return { success: true, data: isEligibleForRostering(result.data) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getTrainingComplianceReport(facilityId = null) {
    try {
      const result = await this.getAll(facilityId);
      if (!result.success) return result;

      const report = MANDATORY_TRAINING.map(training => {
        const staffRecords = result.data.map(staff => {
          const record = staff.trainingRecords?.find(t => t.trainingId === training.id);
          return {
            staffId: staff.id,
            staffName: `${staff.firstName} ${staff.lastName}`,
            roleCode: staff.roleCode,
            status: record?.status || 'not_completed',
            completedDate: record?.completedDate || null,
            expiryDate: record?.expiryDate || null,
            score: record?.score || null
          };
        });

        return {
          trainingId: training.id,
          trainingName: training.name,
          frequency: training.frequency,
          totalStaff: staffRecords.length,
          completed: staffRecords.filter(r => r.status === 'completed').length,
          expired: staffRecords.filter(r => r.status === 'expired').length,
          notCompleted: staffRecords.filter(r => r.status === 'not_completed').length,
          complianceRate: staffRecords.length > 0 ? ((staffRecords.filter(r => r.status === 'completed').length / staffRecords.length) * 100).toFixed(1) : 0,
          staffRecords
        };
      });

      return { success: true, data: report };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async updateTrainingRecord(staffId, trainingId, trainingData) {
    try {
      const result = await this.getById(staffId);
      if (!result.success) return result;

      const profile = result.data;
      const recordIndex = profile.trainingRecords.findIndex(t => t.trainingId === trainingId);

      if (recordIndex === -1) return { success: false, error: 'Training record not found' };

      profile.trainingRecords[recordIndex] = { ...profile.trainingRecords[recordIndex], ...trainingData, status: 'completed' };

      return await this.update(staffId, { trainingRecords: profile.trainingRecords });
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getStaffByRole(facilityId, roleCode = null) {
    try {
      const result = await this.getAll(facilityId);
      if (!result.success) return result;

      let staff = result.data;
      if (roleCode) staff = staff.filter(s => s.roleCode === roleCode);

      const summary = Object.keys(STAFF_ROLES).reduce((acc, code) => {
        acc[code] = staff.filter(s => s.roleCode === code).length;
        return acc;
      }, {});

      return { success: true, data: { staff, summary, total: staff.length } };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getWorkforceStats(facilityId = null) {
    try {
      const result = await this.getAll(facilityId);
      if (!result.success) return result;

      const staff = result.data;
      const alertsResult = await this.getAllCredentialAlerts(facilityId);

      return {
        success: true,
        data: {
          totalStaff: staff.length,
          active: staff.filter(s => s.status === 'active').length,
          onLeave: staff.filter(s => s.status === 'on_leave').length,
          permanent: staff.filter(s => s.employmentType === 'permanent').length,
          casual: staff.filter(s => s.employmentType === 'casual').length,
          agency: staff.filter(s => s.employmentType === 'agency').length,
          contract: staff.filter(s => s.employmentType === 'contract').length,
          byRole: Object.keys(STAFF_ROLES).reduce((acc, code) => { acc[code] = staff.filter(s => s.roleCode === code).length; return acc; }, {}),
          credentialAlerts: alertsResult.success ? {
            total: alertsResult.data.alerts.length,
            expired: alertsResult.data.expired.length,
            critical: alertsResult.data.critical.length,
            warning: alertsResult.data.warning.length,
            blockedStaff: alertsResult.data.staffBlocked.length
          } : null
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new HRService();
